// src/components/SidebarItem.js
import React, { useRef, useState, useEffect } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { NavLink, useNavigate } from 'react-router-dom';

function HoveredSubMenuItem({ icon, text, path, active }) {
  return (
    <NavLink
      to={path}
      className={`my-2 rounded-md p-2 ${
        active ? 'bg-gray-300' : 'hover:bg-indigo-50'
      } flex items-center`}
      title={text}
    >
      <span className="text-primary-500 h-5 w-5">{icon}</span>
      <span className="text-primary-500 ml-3 w-28 text-start">{text}</span>
    </NavLink>
  );
}

export default function SidebarItem({
  icon,
  text,
  path = '#',
  expanded = false,
  subMenu = null,
}) {
  const navigate = useNavigate();
  const itemRef = useRef();
  const [expandSubMenu, setExpandSubMenu] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (expanded) {
      setExpandSubMenu(false);
    }
  }, [expanded]);

  useEffect(() => {
    if (expandSubMenu && itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      setCoords({ top: rect.top, left: rect.right });
    }
  }, [expandSubMenu]);

  const handleClick = (e) => {
    if (subMenu) {
      e.preventDefault();
      setExpandSubMenu((prev) => !prev);
    } else {
      navigate(path);
    }
  };

  return (
    <li ref={itemRef} className="relative">
      <NavLink
        to={path}
        onClick={handleClick}
        title={!expanded ? text : undefined}
        className={({ isActive }) =>
          `group my-1 flex w-full cursor-pointer items-center rounded-md px-3 py-2 font-medium transition-colors ${
            isActive && !subMenu
              ? 'text-primary-500 bg-gradient-to-tr from-indigo-200 to-indigo-100'
              : 'text-gray-600 hover:bg-indigo-50'
          } ${!expanded ? 'hidden sm:flex' : ''}`
        }
      >
        <span className="h-5 w-5">{icon}</span>
        <span
          className={`overflow-hidden text-start transition-all ${
            expanded ? 'ml-3 w-44' : 'w-0'
          }`}
        >
          {text}
        </span>
        {subMenu && expanded && (
          <ChevronRightIcon
            className={`absolute right-2 top-2 h-4 w-4 transition-transform ${
              expandSubMenu ? 'rotate-90' : 'rotate-0'
            }`}
          />
        )}
      </NavLink>

      {/* Expanded sidebar nested submenu */}
      {subMenu && expanded && (
        <ul
          className="pl-6 overflow-hidden transition-[height]"
          style={{
            height: expandSubMenu ? `${subMenu.length * 40 + 10}px` : '0px',
          }}
        >
          {subMenu.map((item, idx) => (
            <SidebarItem key={idx} {...item} expanded={expanded} />
          ))}
        </ul>
      )}

      {/* Collapsed sidebar flyout submenu */}
      {!expanded && subMenu && expandSubMenu && (
        <ul
          className="fixed bg-white border rounded-md shadow-lg z-50"
          style={{
            top: coords.top,
            left: coords.left,
            width: '12rem',
          }}
        >
          {subMenu.map((item, idx) => (
            <li key={idx}>
              <HoveredSubMenuItem
                icon={item.icon}
                text={item.text}
                path={item.path}
                active={false}
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
